import { CandidateStudyProgram, StudyProgram } from '~/services/CSPService';
import {
    CompositionComparerBuilder,
    DateComparerBuilder,
    StringComparerBuilder,
} from '../helpers/comparison';
import { SchoolVerification, SchoolVerificationState } from '~/services/types/school';
import { CandidateJobBuilder } from './CandidateJobBuilder';
import { Candidate } from '~/services/CandidateService';
import { isWithinInterval, lastDayOfMonth, set } from 'date-fns';
import StudyProgramLevelComparer from '../helpers/comparison/StudyProgramLevelComparer';

export class CandidateStudyProgramHelper {
    private static stringComparer = new StringComparerBuilder<CandidateStudyProgram>();
    private static dateComparer = new DateComparerBuilder<CandidateStudyProgram>();

    private static startDateComparer = this.dateComparer.buildComparer(x => x.start, false, true);
    private static endDateComparer = this.dateComparer.buildComparer(x => x.end, false, true);
    private static schoolNameComparer = this.stringComparer.buildComparer(
        x => (x.studyProgram && x.studyProgram.school && x.studyProgram.school.name) || '',
        true
    );

    private static comparer = new CompositionComparerBuilder<CandidateStudyProgram>().buildComparer(
        this.endDateComparer,
        this.startDateComparer,
        this.schoolNameComparer
    );

    public static sortPrograms(studyPrograms: CandidateStudyProgram[]): CandidateStudyProgram[] {
        return studyPrograms.sort((a, b) => {
            return a.start || b.start
                ? this.comparer.compare(a, b)
                : this.schoolNameComparer.compare(a, b);
        });
    }

    public static cloneCandidateWithMergedPrograms(
        candidate: Candidate | null,
        verifications: SchoolVerification[] | null
    ): Candidate | null {
        if (!candidate) {
            return candidate;
        }

        return {
            ...candidate,
            studyPrograms: CandidateStudyProgramHelper.mergePrograms(
                candidate && candidate.studyPrograms,
                verifications
            ),
        };
    }

    public static mergePrograms(
        programs: CandidateStudyProgram[] | null,
        verifications: SchoolVerification[] | null
    ): CandidateStudyProgram[] {
        verifications = [...(verifications || [])];
        programs = [...(programs || [])];

        this.updateVerifications(programs, verifications);
        programs.push(...this.buildMissingPrograms(programs, verifications));

        return programs;
    }

    private static updateVerifications(
        existingPrograms: CandidateStudyProgram[],
        verifications: SchoolVerification[]
    ): void {
        for (const program of existingPrograms) {
            const verification = verifications.find(
                x => x.schoolId === (program.studyProgram && program.studyProgram.schoolId)
            );

            program.verification = verification;
        }
    }

    private static buildMissingPrograms(
        existingPrograms: CandidateStudyProgram[],
        verifications: SchoolVerification[]
    ): CandidateStudyProgram[] {
        const newStudyPrograms: CandidateStudyProgram[] = [];
        for (const verification of verifications) {
            const program = existingPrograms.find(
                x => (x.studyProgram ? x.studyProgram.schoolId : null) == verification.schoolId
            );

            if (program) {
                continue;
            }

            newStudyPrograms.push(this.buildStudyProgram(verification));
        }

        return newStudyPrograms;
    }

    private static buildStudyProgram(verification: SchoolVerification): CandidateStudyProgram {
        return {
            id: CandidateJobBuilder.generateTempVerificationProgramId(),
            programId: '',
            campusId: undefined,
            studyProgram: {
                id: '',
                schoolId: verification.schoolId,
                customSchoolName: null,
                description: '',
                name: '',
                levelId: '',
                isVerified: false,
                fields: [],
                school: {
                    id: verification.schoolId,
                    name: verification.school && verification.school.name,
                    logo: verification.school && verification.school.logo,
                    webAddress: '',
                },
            } as StudyProgram,
            verification,
        } as CandidateStudyProgram;
    }

    // calculateSchoolIdOfMostRelevance is a function that calculates the schoolId of most relevance for a candidate.
    static calculateSchoolIdOfMostRelevance(data: CandidateStudyProgram[]): number | null {
        // The school of most relevance is the following:
        // 1. The current active school with a verification
        //   - If there are multiple active schools with a verification, then just choose based on level.
        // 2. The current active school without a verification
        // 3. The most recent school with a verification
        // 4. The most recent school without a verification

        if (data.length === 0) {
            return null;
        }

        // Select all the education with a school registered in the system
        const schools = data.filter(
            studyProgram => studyProgram.studyProgram && studyProgram.studyProgram.schoolId !== null
        );

        const now = new Date();

        const activeSchools = schools.filter(
            studyProgram =>
                studyProgram.start &&
                studyProgram.end &&
                isWithinInterval(now, {
                    start: new Date(studyProgram.start),
                    end: new Date(
                        set(lastDayOfMonth(studyProgram.end), {
                            hours: 23,
                            minutes: 59,
                            seconds: 59,
                        })
                    ),
                })
        );

        const dateComparer = new DateComparerBuilder<CandidateStudyProgram>();
        const endDateComparerer = dateComparer.buildComparer(csp => csp.end, false);
        const studyProgramLevelComparer = new StudyProgramLevelComparer();
        const sortByLevel = (a: CandidateStudyProgram, b: CandidateStudyProgram) => {
            return (
                studyProgramLevelComparer.compare(a.studyProgram!.level, b.studyProgram!.level) ||
                endDateComparerer.compare(a, b)
            );
        };

        const activeSchoolsWithVerification = activeSchools.filter(
            studyProgram =>
                studyProgram.verification &&
                studyProgram.verification.state === SchoolVerificationState.verified
        );
        if (activeSchoolsWithVerification.length > 0) {
            if (activeSchoolsWithVerification.length === 1) {
                return activeSchoolsWithVerification[0].studyProgram!.schoolId;
            }

            // If there are multiple active schools with a verification, then just choose based on level.
            const sortedSchools = activeSchoolsWithVerification.sort(sortByLevel);
            // Here we just pick the first one, since it is the one with the highest level. If there are multiple
            // schools with the same level, then it is just random which one we pick.
            return sortedSchools[0].studyProgram!.schoolId;
        }

        const activeSchoolsWithoutVerification = activeSchools.filter(
            studyProgram =>
                !studyProgram.verification ||
                studyProgram.verification.state !== SchoolVerificationState.verified
        );
        if (activeSchoolsWithoutVerification.length > 0) {
            if (activeSchoolsWithoutVerification.length === 1) {
                return activeSchoolsWithoutVerification[0].studyProgram!.schoolId;
            }

            // If there are multiple active schools without a verification, then just choose based on level.
            const sortedSchools = activeSchoolsWithoutVerification.sort(sortByLevel);
            // Here we just pick the first one, since it is the one with the highest level. If there are multiple
            // schools with the same level, then it is just random which one we pick.
            return sortedSchools[0].studyProgram!.schoolId;
        }

        const sortFunc = (a: CandidateStudyProgram, b: CandidateStudyProgram) => {
            if (CandidateJobBuilder.isTempVerificationProgramItem(a)) {
                return 1;
            } else if (CandidateJobBuilder.isTempVerificationProgramItem(b)) {
                return -1;
            }

            const value = endDateComparerer.compare(a, b);
            if (value !== 0) {
                return value;
            }

            return sortByLevel(a, b);
        };

        const schoolsWithVerification = schools
            .filter(
                studyProgram =>
                    studyProgram.verification &&
                    studyProgram.verification.state === SchoolVerificationState.verified
            )
            .sort(sortFunc);

        if (schoolsWithVerification.length > 0) {
            return schoolsWithVerification[0].studyProgram!.schoolId;
        }

        const schoolsWithoutVerification = schools
            .filter(
                studyProgram =>
                    !studyProgram.verification ||
                    studyProgram.verification.state !== SchoolVerificationState.verified
            )
            .sort(sortFunc);

        if (schoolsWithoutVerification.length > 0) {
            return schoolsWithoutVerification[0].studyProgram!.schoolId;
        }

        return null;
    }
}
